import React, { useState, useEffect, useCallback } from "react";
import "./TeacherDashboard.css";
import "../../styles/dashboard-shell.css";
import "./teacher-layout.css";
import {
  User,
  MessageSquare,
  Calendar,
  BarChart3,
  BookOpen,
  Bell,
  Settings,
  TrendingUp,
  Clock,
  Users,
  Target,
  Brain,
  FileText,
  Activity,
  AlertCircle,
  Loader,
  Menu,
  X,
  GraduationCap,
  ClipboardList,
  Lightbulb,
  CheckCircle,
  Search,
  Trash2,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout/Layout";
import Messaging from "../../components/Dashboard/Messaging/Messaging";
import AnnouncementBanner from "../../components/AnnouncementBanner/AnnouncementBanner";
import { getApiBaseUrl } from "../../services/apiBase.js";

// ==================== CONSTANTS ====================
const API_BASE_URL = getApiBaseUrl();
const REFRESH_INTERVAL = 300000; // 5 minutes

const TEACHER_AGENT_PAGES = [
  {
    name: "Student Activity",
    icon: Activity,
    description: "Look up a student by ID and see their quizzes, courses and tutor use",
    color: "#2A4F9E",
    route: "/teacher/student-activity",
  },
  {
    name: "Teaching Assistant",
    icon: Brain,
    description: "Get pedagogical support & teaching strategies",
    color: "#0B1B3F",
    route: "/teacher/agents/tutor",
  },
  {
    name: "Student Analytics",
    icon: BarChart3,
    description: "View student progress & performance",
    color: "#D9950F",
    route: "/teacher/agents/analytics",
  },
  {
    name: "Curriculum Viewer",
    icon: BookOpen,
    description: "Browse curriculum content",
    color: "#2A4F9E",
    route: "/agents/curriculum-viewer",
  },
  {
    name: "Lesson Planner",
    icon: ClipboardList,
    description: "Create comprehensive lesson plans",
    color: "#0B1B3F",
    route: "/teacher/agents/lesson-planner",
  },
  {
    name: "Document Summarizer",
    icon: FileText,
    description: "Summarize educational materials",
    color: "#D9950F",
    route: "/agents/summarize",
  },
  {
    name: "Concept Explainer",
    icon: Lightbulb,
    description: "Get detailed explanations for teaching",
    color: "#2A4F9E",
    route: "/agents/explain",
  },
];

// ==================== UTILITY FUNCTIONS ====================
const getAuthToken = () => {
  return (
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token")
  );
};

const formatDate = (dateString) => {
  if (!dateString) return "Recently";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

// ==================== MAIN COMPONENT ====================
const TeacherDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [students, setStudents] = useState([]);
  const [idInput, setIdInput] = useState("");
  
  // Get teacher info from storage
  const teacherName = sessionStorage.getItem("name") || localStorage.getItem("name") || "Teacher";
  const teacherEmail = sessionStorage.getItem("email") || localStorage.getItem("email");

  // ==================== DATA FETCHING ====================
  const fetchDashboardData = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/teacher/dashboard`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      if (!response.ok) {
        throw new Error("Failed to fetch dashboard data");
      }

      const data = await response.json();
      setDashboardData(data);
      setError(null);
    } catch (err) {
      console.error("Dashboard error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  const fetchStudents = useCallback(async () => {
    const token = getAuthToken();
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/teacher/students`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStudents(data);
      }
    } catch (err) {
      console.error("Error fetching students:", err);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    fetchStudents();
    const interval = setInterval(fetchDashboardData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchDashboardData, fetchStudents]);

  const openStudent = (studentId) =>
    navigate(`/teacher/student-activity/${encodeURIComponent(studentId)}`);

  const addStudent = (e) => {
    e.preventDefault();
    const id = idInput.trim();
    if (id) openStudent(id);
  };

  const removeStudent = async (student) => {
    if (!window.confirm(`Remove ${student.name} from your list? Their data is not deleted.`)) return;
    try {
      await fetch(`${API_BASE_URL}/api/teacher/students/${encodeURIComponent(student.student_id)}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setStudents((list) => list.filter((x) => x.student_id !== student.student_id));
    } catch (err) {
      console.error("Error removing student:", err);
    }
  };

  // ==================== RENDER FUNCTIONS ====================
  const renderOverview = () => (
    <div className="dashboard-overview">
      {/* Quick Stats */}
      <div className="stats-grid">
        <div className="stat-card purple">
          <div className="stat-icon">
            <Users size={24} />
          </div>
          <div className="stat-content">
            <h3>{students.length || 0}</h3>
            <p>My Students</p>
          </div>
        </div>

        <div className="stat-card blue">
          <div className="stat-icon">
            <FileText size={24} />
          </div>
          <div className="stat-content">
            <h3>{dashboardData?.total_quizzes || 0}</h3>
            <p>Lesson Notes Created</p>
          </div>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">
            <Brain size={24} />
          </div>
          <div className="stat-content">
            <h3>{TEACHER_AGENT_PAGES.length}</h3>
            <p>AI Tools Available</p>
          </div>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">
            <TrendingUp size={24} />
          </div>
          <div className="stat-content">
            <h3>{dashboardData?.average_score || 0}%</h3>
            <p>Class Average</p>
          </div>
        </div>
      </div>

      {/* AI Teaching Tools */}
      <div className="section">
        <div className="section-header">
          <h2>
            <Brain size={20} />
            AI Teaching Tools
          </h2>
          <p>Click any tool below to get started</p>
        </div>

        <div className="agent-grid">
          {TEACHER_AGENT_PAGES.map((agent, index) => (
            <div
              key={index}
              className="agent-card"
              onClick={() => navigate(agent.route)}
              style={{ borderLeftColor: agent.color }}
            >
              <div className="agent-icon" style={{ backgroundColor: agent.color }}>
                <agent.icon size={24} />
              </div>
              <div className="agent-info">
                <h3>{agent.name}</h3>
                <p>{agent.description}</p>
              </div>
              <div className="agent-arrow">→</div>
            </div>
          ))}
        </div>
      </div>

      {/* My students */}
      <div className="section">
        <div className="section-header">
          <h2>
            <Users size={20} />
            My Students
          </h2>
          {students.length > 0 && (
            <button className="view-all-btn" onClick={() => setActiveTab("students")}>
              Manage →
            </button>
          )}
        </div>

        {students.length > 0 ? (
          <div className="students-preview">
            {students.slice(0, 6).map((student) => (
              <div key={student.id} className="student-preview-card" onClick={() => openStudent(student.student_id)}>
                <div className="student-avatar">
                  <User size={20} />
                </div>
                <div className="student-info">
                  <h4>{student.name}</h4>
                  <p className="student-id">ID: {student.student_id}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="tl-empty">
            <p>
              You haven't added any students yet. Enter a student's ID to see their activity — they'll be saved here
              for next time.
            </p>
            <button className="eg-btn eg-btn-accent" onClick={() => setActiveTab("students")}>
              Add a student by ID
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const renderStudentAnalytics = () => (
    <div className="dashboard-section">
      <div className="section-header">
        <h2>
          <Users size={20} />
          My Students
        </h2>
        <p>You only see students whose student ID you know.</p>
      </div>

      <form className="tl-add" onSubmit={addStudent} role="search">
        <Search size={18} aria-hidden="true" />
        <input
          value={idInput}
          onChange={(e) => setIdInput(e.target.value)}
          placeholder="Enter a student ID, e.g. S-2026-AB12"
          aria-label="Student ID"
          autoComplete="off"
          spellCheck={false}
        />
        <button type="submit" className="eg-btn eg-btn-accent" disabled={!idInput.trim()}>
          View &amp; add
        </button>
      </form>

      {students.length > 0 ? (
        <div className="students-list">
          {students.map((student) => (
            <div key={student.id} className="student-card" onClick={() => openStudent(student.student_id)}>
              <div className="student-avatar">
                <User size={24} />
              </div>
              <div className="student-info">
                <h3>{student.name}</h3>
                <p>
                  ID {student.student_id}
                  {student.class ? ` · ${student.class}` : ""}
                </p>
              </div>
              <button
                type="button"
                className="tl-remove"
                title="Remove from my list"
                aria-label={`Remove ${student.name} from my list`}
                onClick={(e) => {
                  e.stopPropagation();
                  removeStudent(student);
                }}
              >
                <Trash2 size={16} />
              </button>
              <div className="student-arrow">
                <BarChart3 size={20} />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="empty-state">
          <Users size={48} />
          <p>No students added yet</p>
          <small>Type a student's ID above. Once you've looked them up, they stay on this list.</small>
        </div>
      )}
    </div>
  );

  const renderMessages = () => (
    <div className="dashboard-section">
      <Messaging />
    </div>
  );

  const renderProfile = () => (
    <div className="dashboard-section">
      <div className="profile-section">
        <div className="profile-header">
          <div className="profile-avatar">
            <User size={48} />
          </div>
          <div className="profile-info">
            <h2>{teacherName}</h2>
            <p>{teacherEmail}</p>
            <span className="profile-badge">Teacher Account</span>
          </div>
        </div>

        <div className="profile-stats">
          <div className="profile-stat">
            <Users size={20} />
            <div>
              <h4>{students.length}</h4>
              <p>Students</p>
            </div>
          </div>
          <div className="profile-stat">
            <Brain size={20} />
            <div>
              <h4>{TEACHER_AGENT_PAGES.length}</h4>
              <p>AI Tools Available</p>
            </div>
          </div>
        </div>

        <div className="profile-actions">
          <button
            className="btn-primary"
            onClick={() => navigate("/teacher/profile/edit")}
          >
            <Settings size={16} />
            Edit Profile
          </button>
          <button
            className="btn-secondary"
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              navigate("/login");
            }}
          >
            Logout
          </button>
        </div>
      </div>
    </div>
  );

  // ==================== MAIN RENDER ====================
  if (loading && !dashboardData) {
    return (
      <Layout>
        <div className="dashboard-loading">
          <Loader className="spinner" size={48} />
          <p>Loading dashboard...</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="student-dashboard teacher-dashboard">
        {/* Sidebar */}
        <aside className={`dashboard-sidebar ${sidebarOpen ? "open" : "closed"}`}>
          <div className="sidebar-header">
            <h2>Dashboard</h2>
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>

          <nav className="sidebar-nav">
            <button
              className={`nav-item ${activeTab === "overview" ? "active" : ""}`}
              onClick={() => setActiveTab("overview")}
            >
              <Brain size={20} />
              <span>AI Tools</span>
            </button>

            <button
              className={`nav-item ${activeTab === "students" ? "active" : ""}`}
              onClick={() => setActiveTab("students")}
            >
              <Users size={20} />
              <span>Students</span>
            </button>

            <button
              className={`nav-item ${activeTab === "messages" ? "active" : ""}`}
              onClick={() => setActiveTab("messages")}
            >
              <MessageSquare size={20} />
              <span>Messages</span>
              {notifications.length > 0 && (
                <span className="notification-badge">{notifications.length}</span>
              )}
            </button>

            <button
              className={`nav-item ${activeTab === "profile" ? "active" : ""}`}
              onClick={() => setActiveTab("profile")}
            >
              <User size={20} />
              <span>Profile</span>
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          <div className="dashboard-header">
            <div>
              <h1>Welcome, {teacherName}! 👋</h1>
              <p>Select an AI tool below or view your students</p>
            </div>

            <div className="header-actions">
              <button 
                className="icon-button" 
                title="Quick Access"
                onClick={() => navigate("/teacher/agents/tutor")}
              >
                <Brain size={20} />
              </button>
            </div>
          </div>

          {error && (
            <div className="error-banner">
              <AlertCircle size={20} />
              <p>{error}</p>
              <button onClick={() => setError(null)}>×</button>
            </div>
          )}

          <div className="dashboard-content">
            <AnnouncementBanner />
            {activeTab === "overview" && renderOverview()}
            {activeTab === "students" && renderStudentAnalytics()}
            {activeTab === "messages" && renderMessages()}
            {activeTab === "profile" && renderProfile()}
          </div>
        </main>
      </div>
    </Layout>
  );
};

export default TeacherDashboard;
